import { useEffect } from "react"
import { Box, IconButton, Typography } from "@mui/material"
import type { PortfolioShot } from "./projects"

type Props = {
  shots: PortfolioShot[]
  index: number
  onClose: () => void
  onPrev: () => void
  onNext: () => void
}

export function Lightbox({ shots, index, onClose, onPrev, onNext }: Props) {
  const shot = shots[index]
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose()
      if (e.key === "ArrowLeft") onPrev()
      if (e.key === "ArrowRight") onNext()
    }
    window.addEventListener("keydown", onKey)
    const prev = document.body.style.overflow
    document.body.style.overflow = "hidden"
    return () => {
      window.removeEventListener("keydown", onKey)
      document.body.style.overflow = prev
    }
  }, [onClose, onPrev, onNext])

  if (!shot) return null

  return (
    <Box
      role="dialog"
      aria-modal
      aria-label={shot.caption}
      onClick={onClose}
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: 40,
        bgcolor: "rgba(2,4,10,0.92)",
        display: "grid",
        placeItems: "center",
        p: { xs: 1.5, md: 3 },
      }}
    >
      <Box
        onClick={(e) => e.stopPropagation()}
        sx={{
          width: "min(1200px, 100%)",
          position: "relative",
        }}
      >
        <Box
          component="img"
          src={shot.src}
          alt={shot.alt}
          sx={{
            width: "100%",
            height: "auto",
            maxHeight: "78vh",
            objectFit: "contain",
            display: "block",
            border: "1px solid rgba(140,190,255,0.25)",
            bgcolor: "#000",
          }}
        />
        <Typography
          sx={{
            mt: 1.25,
            letterSpacing: "0.16em",
            fontSize: "0.7rem",
            color: "rgba(180,210,255,0.8)",
            textAlign: "center",
          }}
        >
          {shot.caption} ({index + 1}/{shots.length})
        </Typography>
        <Box sx={{ display: "flex", justifyContent: "center", gap: 1.5, mt: 1.5 }}>
          <IconButton
            aria-label="Previous screenshot"
            onClick={onPrev}
            sx={{ color: "#cfe6ff", border: "1px solid rgba(140,190,255,0.3)" }}
          >
            ‹
          </IconButton>
          <IconButton
            aria-label="Close gallery"
            onClick={onClose}
            sx={{ color: "#cfe6ff", border: "1px solid rgba(140,190,255,0.3)" }}
          >
            ✕
          </IconButton>
          <IconButton
            aria-label="Next screenshot"
            onClick={onNext}
            sx={{ color: "#cfe6ff", border: "1px solid rgba(140,190,255,0.3)" }}
          >
            ›
          </IconButton>
        </Box>
      </Box>
    </Box>
  )
}
